import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { string, bool, func, shape } from 'prop-types';
import { useStore } from '@thd-nucleus/experience-context';
import { useDataModel } from '@thd-nucleus/data-sources';
import {
  Alert,
  DrawerHeader,
  DrawerBody,
  Drawer,
  Typography
} from '@one-thd/sui-atomic-components';
import { dataModel } from './dataModel';
import { ElligibleItems } from '../elligible-items/ElligibleItems';
import { CurrentItem } from '../current-item/CurrentItem';
import { SwapContextProvider } from '../SwapContext';

const SwapDrawer = ({
  itemId,
  promoId,
  openSwapDrawer,
  onClose,
  fetch,
  onSwap,
  hideCurrentSelectedItem,
  currentItemSelectable,
  selectionButtonContent,
  openInQuickViewDescription,
  openDetailsInNewTab,
  hideQuickViewMask,
  itemIndex,
  drawerRootProps,
  openCurrentItemInQuickView,
  filterResponse
}) => {
  const { storeId } = useStore();
  const [currentItem, setCurrentItem] = useState({});
  const { data, loading, error } = useDataModel('packageSwapRecs', {
    variables: {
      itemId,
      storeId,
      promoId,
      filterResponse
    },
    ssr: false,
    skip: !itemId || !fetch
  });

  useEffect(() => {
    if (!loading && openSwapDrawer) {
      if (data?.packageSwapRecs?.products) {
        const output = {
          position: itemIndex
        };
        window.LIFE_CYCLE_EVENT_BUS.trigger('swap-item.success', output);

      } else {
        const message = error?.message || 'n/a';
        const code = error?.graphQLErrors?.[0]?.extensions?.id || 500;
        window.LIFE_CYCLE_EVENT_BUS.trigger('swap-item.error', { message, code });
      }
    }
  }, [data, loading, openSwapDrawer, error]);

  if (!data) {
    return null;
  }

  const swappableProducts = data?.packageSwapRecs?.products?.filter((product) => product.itemId !== itemId);

  return createPortal(
    <div data-component="SwapDrawer">
      <Drawer
        open={openSwapDrawer && !loading && fetch}
        onClose={onClose}
        DrawerRootProps={drawerRootProps}
      >
        <div className="sui-bg-primary sui-shadow-md sui-mb-2 sui-sticky sui-top-0 sui-z-10">
          <DrawerHeader
            className="sui-h-12 sui-grid"
            title={(
              <div className="sui-p-2">
                <Typography
                  component="span"
                  variant="h4"
                  color="primary"
                  weight="bold"
                >Make New Selection
                </Typography>
              </div>
            )}
            onClose={onClose}
          />
        </div>
        <SwapContextProvider value={{
          onSwap,
          currentItem,
          selectionButtonContent,
          setCurrentItem,
          openSwapDrawer,
          onClose
        }}
        >
          <DrawerBody
            className="sui-px-4 sui-pt-2 sui-pb-4"
            name="swap-products"
          >
            {!hideCurrentSelectedItem
              && (
                <CurrentItem
                  itemId={itemId}
                  storeId={storeId}
                  currentItemSelectable={currentItemSelectable}
                  openCurrentItemInQuickView={openCurrentItemInQuickView}
                  openDetailsInNewTab={openDetailsInNewTab}
                  hideQuickViewMask={hideQuickViewMask}
                />
              )}
            <div className="sui-pt-6">
              <section>
                {data?.packageSwapRecs?.products?.length > 0
                  ? (
                    <>
                      <Typography
                        height="tight"
                        variant="h5"
                      >Select {hideCurrentSelectedItem ? 'an Item' : 'a Different Item'}
                      </Typography>
                      <ElligibleItems
                        products={swappableProducts}
                        anchorItem={itemId}
                        openInQuickViewDescription={openInQuickViewDescription}
                        openDetailsInNewTab={openDetailsInNewTab}
                        hideQuickViewMask={hideQuickViewMask}
                      />
                    </>
                  )
                  : (
                    <Alert status="error">
                      <Typography>Sorry! No eligible items available to swap.</Typography>
                    </Alert>
                  )}
              </section>
            </div>
          </DrawerBody>
        </SwapContextProvider>
      </Drawer>
    </div>,
    document.body
  );
};

SwapDrawer.propTypes = {
  itemId: string.isRequired,
  promoId: string,
  openSwapDrawer: bool.isRequired,
  onClose: func.isRequired,
  fetch: bool.isRequired,
  onSwap: func.isRequired,
  hideCurrentSelectedItem: bool.isRequired,
  selectionButtonContent: string,
  currentItemSelectable: bool,
  openInQuickViewDescription: bool,
  openDetailsInNewTab: bool,
  hideQuickViewMask: bool,
  itemIndex: string,
  drawerRootProps: shape({}),
  openCurrentItemInQuickView: bool,
  filterResponse: bool
};

SwapDrawer.defaultProps = {
  promoId: null,
  selectionButtonContent: null,
  currentItemSelectable: false,
  openInQuickViewDescription: false,
  openDetailsInNewTab: false,
  hideQuickViewMask: false,
  itemIndex: 'n/a',
  drawerRootProps: {},
  openCurrentItemInQuickView: false,
  filterResponse: true
};

SwapDrawer.displayName = 'SwapDrawer';

SwapDrawer.dataModel = dataModel;

export { SwapDrawer };
